import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faSpinner, faMicrophone, faTriangleExclamation, faPlus } from '@fortawesome/free-solid-svg-icons';
import { CustomDropdown, useNotifications } from '@medprompt/shared';
import { NotesModeEnum } from '../enums/consultationStatusEnum';
import { DEFAULT_SPEAKERS_COUNT, speakersCountOptions, getSpeakersCountDisplay } from './speakersCountOptions';
import { EncounterType } from '../enums/encounterTypeEnum';
import EncounterTypeRadios from './EncounterTypeRadios';
import { ConsultationService, sharedApiClient } from '../fetcher';
import NotesTemplateEditorModal from './NotesTemplateEditorModal';
import MicrophoneMonitor from './MicrophoneMonitor';
import MicrophoneCheckService from '../services/MicrophoneCheckService';
import '@medprompt/shared/src/common/dropdowns.css';
import '../documents/DocumentsListView.css';
import './list/ScribeListView.css';
import './ConsultationSetupModal.css';

const ADD_TEMPLATE_ID = '__add_template__';

const NewConsultationModal = ({ isOpen, onClose, onStart, loading }) => {
  const [nhsId, setNhsId] = useState('');
  const [templateId, setTemplateId] = useState('');
  const [notesMode, setNotesMode] = useState('MEDIUM');
  const [speakersCount, setSpeakersCount] = useState(DEFAULT_SPEAKERS_COUNT);
  const [encounterType, setEncounterType] = useState(EncounterType.FACE_TO_FACE.value);
  const [error, setError] = useState('');
  const [selectedDeviceId, setSelectedDeviceId] = useState(MicrophoneCheckService.getLastDeviceId() || '');
  const [micCanRecord, setMicCanRecord] = useState(false);

  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [showEditorModal, setShowEditorModal] = useState(false);

  const [templateDropdownOpen, setTemplateDropdownOpen] = useState(false);
  const [notesModeDropdownOpen, setNotesModeDropdownOpen] = useState(false);
  const [speakersCountDropdownOpen, setSpeakersCountDropdownOpen] = useState(false);
  
  const templateDropdownRef = useRef(null);
  const notesModeDropdownRef = useRef(null);
  const speakersCountDropdownRef = useRef(null);

  const consultationService = useMemo(() => new ConsultationService(sharedApiClient), []);
  const { addNotification } = useNotifications();

  const handleMicReady = useCallback(({ deviceId, canRecord }) => {
    if (deviceId) setSelectedDeviceId(deviceId);
    setMicCanRecord(!!canRecord);
  }, []);

  const handleMicError = useCallback((status) => {
    setMicCanRecord(false);
    if (status?.message) {
      setError(status.message);
    }
  }, []);

  const handleDeviceChange = useCallback((deviceId) => {
    setSelectedDeviceId(deviceId || '');
  }, []);

  const fetchTemplates = async () => {
    try {
      setLoadingTemplates(true);
      const fetchedTemplates = await consultationService.fetchNotesTemplates();
      setTemplates(fetchedTemplates || []);
      if (fetchedTemplates?.length > 0 && !templateId) {
        setTemplateId(fetchedTemplates[0].templateId);
      }
    } catch (err) {
      console.error('Error fetching notes templates:', err);
    } finally {
      setLoadingTemplates(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchTemplates();
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setNhsId('');
      setTemplateId('');
      setNotesMode('MEDIUM');
      setSpeakersCount(DEFAULT_SPEAKERS_COUNT);
      setEncounterType(EncounterType.FACE_TO_FACE.value);
      setError('');
      setMicCanRecord(false);
      setSelectedDeviceId(MicrophoneCheckService.getLastDeviceId() || '');
      setTemplateDropdownOpen(false);
      setNotesModeDropdownOpen(false);
      setSpeakersCountDropdownOpen(false);
    }
  }, [isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!nhsId.trim()) {
      setError('Please enter an NHS Number');
      return;
    }

    if (!micCanRecord) {
      setError('Microphone is not ready. Please select a working microphone.');
      return;
    }

    try {
      await onStart(nhsId.trim(), templateId, notesMode, speakersCount, encounterType, selectedDeviceId || null);
    } catch (err) {
      if (err.status === 400) {
        setError('Invalid NHS Number. Please check and try again.');
      } else {
        setError('Failed to start recording. Please try again.');
      }
    }
  };

  const templateOptions = useMemo(() => {
    const options = templates.map(t => ({ 
      id: t.templateId, 
      name: t.title 
    }));
    options.push({ 
      id: ADD_TEMPLATE_ID, 
      name: 'Add template',
      icon: faPlus
    });
    return options;
  }, [templates]);

  const getSelectedTemplateName = () => {
    if (!templateId) return 'Select template';
    const template = templates.find(t => t.templateId === templateId);
    return template ? template.title : 'Select template';
  };

  const getSelectedNotesModeName = () => {
    const mode = NotesModeEnum[notesMode];
    return mode ? mode.description : 'Select detail level';
  };

  const handleTemplateSelect = (option) => {
    if (option.id === ADD_TEMPLATE_ID) {
      setTemplateDropdownOpen(false);
      setShowEditorModal(true);
    } else {
      setTemplateId(option.id);
      setTemplateDropdownOpen(false);
    }
  };

  const handleNotesModeSelect = (option) => {
    setNotesMode(option.value);
    setNotesModeDropdownOpen(false);
  };

  const handleSpeakersCountSelect = (option) => {
    setSpeakersCount(option.value);
    setSpeakersCountDropdownOpen(false);
  };

  const handleTemplateSave = async (templateData) => {
    try {
      const createData = {
        templateId: null,
        title: templateData.title,
        content: templateData.content
      };
      await consultationService.createNotesTemplate(createData);
      
      addNotification('new-consultation', 'Notes template created successfully', { isError: false });
      
      setShowEditorModal(false);
      
      const fetchedTemplates = await consultationService.fetchNotesTemplates();
      setTemplates(fetchedTemplates || []);
      
      const newTemplate = fetchedTemplates?.find(t => t.title === templateData.title);
      if (newTemplate) {
        setTemplateId(newTemplate.templateId);
      }
    } catch (err) {
      console.error('Error creating notes template:', err);
      addNotification('new-consultation', 'Failed to create notes template', { isError: true });
      throw err;
    }
  };

  const handleCloseEditor = () => {
    setShowEditorModal(false);
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-container consultation-setup-modal">
        <div className="modal-header">
          <h2>
            <FontAwesomeIcon icon={faMicrophone} style={{ marginRight: '10px' }} />
            New Consultation
          </h2>
          <button className="close-button" onClick={onClose} disabled={loading}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-content">
            <div className="consultation-setup-columns">
              <div className="consultation-setup-column">
                <div className="consultation-setup-column-content">
                  <div className="form-group">
                    <label htmlFor="nhs-id">NHS Number</label>
                    <input
                      id="nhs-id"
                      type="text"
                      value={nhsId}
                      onChange={(e) => setNhsId(e.target.value)}
                      placeholder="Enter patient NHS Number"
                      disabled={loading}
                      autoFocus
                      className="form-input"
                    />
                  </div>

                  <div className="form-group">
                    <label>Notes Template</label>
                    <CustomDropdown
                      label="Notes Template"
                      selected={loadingTemplates ? 'Loading...' : getSelectedTemplateName()}
                      options={templateOptions}
                      isOpen={templateDropdownOpen}
                      toggleDropdown={() => {
                        if (!loading && !loadingTemplates) {
                          setTemplateDropdownOpen(!templateDropdownOpen);
                          setNotesModeDropdownOpen(false);
                          setSpeakersCountDropdownOpen(false);
                        }
                      }}
                      onSelect={handleTemplateSelect}
                      containerRef={templateDropdownRef}
                      keyExtractor={(option) => option.id}
                      disabled={loading || loadingTemplates}
                    />
                  </div>

                  <div className="form-group">
                    <label>Notes Detail Level</label>
                    <CustomDropdown
                      label="Notes Detail Level"
                      selected={getSelectedNotesModeName()}
                      options={Object.values(NotesModeEnum)}
                      isOpen={notesModeDropdownOpen}
                      toggleDropdown={() => {
                        if (!loading) {
                          setNotesModeDropdownOpen(!notesModeDropdownOpen);
                          setTemplateDropdownOpen(false);
                          setSpeakersCountDropdownOpen(false);
                        }
                      }}
                      onSelect={handleNotesModeSelect}
                      containerRef={notesModeDropdownRef}
                      keyExtractor={(option) => option.value}
                      disabled={loading}
                    />
                  </div>
                </div>
              </div>

              <div className="consultation-setup-column">
                <div className="consultation-setup-column-content">
                  <div className="form-group">
                    <label>Number of Speakers</label>
                    <CustomDropdown
                      label="Number of Speakers"
                      selected={getSpeakersCountDisplay(speakersCount)}
                      options={speakersCountOptions}
                      isOpen={speakersCountDropdownOpen}
                      toggleDropdown={() => {
                        if (!loading) {
                          setSpeakersCountDropdownOpen(!speakersCountDropdownOpen);
                          setTemplateDropdownOpen(false);
                          setNotesModeDropdownOpen(false);
                        }
                      }}
                      onSelect={handleSpeakersCountSelect}
                      containerRef={speakersCountDropdownRef}
                      keyExtractor={(option) => option.value}
                      disabled={loading}
                    />
                  </div>

                  <EncounterTypeRadios
                    value={encounterType}
                    onChange={setEncounterType}
                    disabled={loading}
                  />

                  <div className="form-group">
                    <MicrophoneMonitor
                      mode="preflight"
                      active={isOpen && !showEditorModal && !loading}
                      preferredDeviceId={selectedDeviceId || null}
                      onReady={handleMicReady}
                      onError={handleMicError}
                      onDeviceChange={handleDeviceChange}
                      showSilenceHint
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="consultation-setup-notice">
              <div className="consultation-setup-notice-header">
                <FontAwesomeIcon icon={faTriangleExclamation} />
                <strong>Inform the patient</strong>
              </div>
              <p className="consultation-setup-notice-body">
                You must inform the patient that an ambient scribe is in use. If they object, do not start the recording.
              </p>
              <p className="consultation-setup-notice-wording">
                Suggested wording: “I am using an AI scribe to help write my notes. If you would prefer I did not use it, please say so and I will not.”
              </p>
            </div>

            {error && (
              <div className="consultation-setup-error">
                {error}
              </div>
            )}
          </div>
          <div className="modal-actions">
            <button 
              type="button" 
              className="cancel-button" 
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </button>
            <button 
              type="submit" 
              className="header-button header-button-primary"
              disabled={loading || !nhsId.trim() || !templateId || !micCanRecord}
            >
              {loading ? (
                <FontAwesomeIcon icon={faSpinner} spin />
              ) : (
                <>
                  <FontAwesomeIcon icon={faMicrophone} />
                  Start Recording
                </>
              )}
            </button>
          </div>
        </form>
      </div>

      {showEditorModal && (
        <NotesTemplateEditorModal
          template={null}
          onSave={handleTemplateSave}
          onClose={handleCloseEditor}
        />
      )}
    </div>
  );
};

export default NewConsultationModal;
